(function (root, factory) {
  'use strict';

  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.GiftChoiceCore = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const DECISION_STORAGE_KEY = 'gift_system_level_decisions_v2';

  function normalizeGiftOptions(resolvedProducts) {
    if (!Array.isArray(resolvedProducts)) {
      return [];
    }

    const seenHandles = new Set();
    const seenVariantIds = new Set();
    const options = [];

    resolvedProducts.forEach((product) => {
      if (!product || typeof product.handle !== 'string' || !Array.isArray(product.variants)) {
        return;
      }

      const handle = product.handle.trim().toLowerCase();
      const variant = product.variants[0];

      if (
        !handle ||
        !variant ||
        variant.id == null ||
        variant.available !== true
      ) {
        return;
      }

      const variantKey = String(variant.id);

      if (seenHandles.has(handle) || seenVariantIds.has(variantKey)) {
        return;
      }

      seenHandles.add(handle);
      seenVariantIds.add(variantKey);
      options.push({
        handle,
        productId: product.id ?? null,
        variantId: variant.id,
        title: product.title || '',
        image: product.featured_image || product.image?.src || null,
        price: variant.price ?? 0,
        available: true,
      });
    });

    return options;
  }

  function buildOptionSignature(options) {
    if (!Array.isArray(options)) {
      return '';
    }

    const pairs = new Set();

    options.forEach((option) => {
      if (
        !option ||
        typeof option.handle !== 'string' ||
        option.variantId == null
      ) {
        return;
      }

      const handle = option.handle.trim().toLowerCase();
      const variantId = String(option.variantId);

      if (!handle || !variantId) {
        return;
      }

      pairs.add(`${handle}:${variantId}`);
    });

    return Array.from(pairs).sort().join('|');
  }

  function resolveOptionCardinality(options) {
    if (!Array.isArray(options) || options.length === 0) {
      return { mode: 'unavailable', option: null };
    }

    if (options.length === 1) {
      return { mode: 'auto', option: options[0] };
    }

    return { mode: 'choose', option: null };
  }

  function isRecord(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function getScopeKeys(scope, needsSignature) {
    if (
      !isRecord(scope) ||
      typeof scope.cartToken !== 'string' ||
      scope.cartToken.length === 0 ||
      scope.levelNumber == null
    ) {
      return null;
    }

    const levelKey = String(scope.levelNumber);

    if (!levelKey || (needsSignature && typeof scope.signature !== 'string')) {
      return null;
    }

    return {
      cartToken: scope.cartToken,
      levelKey,
    };
  }

  function getNoneDecision(store, scope) {
    const keys = getScopeKeys(scope, true);

    if (!isRecord(store) || !keys) {
      return null;
    }

    const tokenDecisions = store[keys.cartToken];
    const storedDecision = isRecord(tokenDecisions)
      ? tokenDecisions[keys.levelKey]
      : null;

    if (
      !isRecord(storedDecision) ||
      storedDecision.decision !== 'none' ||
      storedDecision.signature !== scope.signature
    ) {
      return null;
    }

    return {
      decision: 'none',
      signature: storedDecision.signature,
    };
  }

  function recordNoneDecision(store, scope) {
    const root = isRecord(store) ? { ...store } : {};
    const keys = getScopeKeys(scope, true);

    if (!keys) {
      return root;
    }

    const currentTokenDecisions = isRecord(root[keys.cartToken])
      ? root[keys.cartToken]
      : {};

    root[keys.cartToken] = {
      ...currentTokenDecisions,
      [keys.levelKey]: {
        decision: 'none',
        signature: scope.signature,
      },
    };

    return root;
  }

  function clearNoneDecision(store, scope) {
    const root = isRecord(store) ? { ...store } : {};
    const keys = getScopeKeys(scope, false);

    if (!keys || !isRecord(root[keys.cartToken])) {
      return root;
    }

    const tokenDecisions = { ...root[keys.cartToken] };

    delete tokenDecisions[keys.levelKey];

    if (Object.keys(tokenDecisions).length === 0) {
      delete root[keys.cartToken];
    } else {
      root[keys.cartToken] = tokenDecisions;
    }

    return root;
  }

  function resolveLevelState(input) {
    const source = isRecord(input) ? input : {};
    const levelNumber = source.levelNumber;
    const options = Array.isArray(source.options) ? source.options : [];
    const cartItems = Array.isArray(source.cartItems) ? source.cartItems : [];
    const decision = source.decision;
    const signature = buildOptionSignature(options);
    const giftLine = levelNumber == null
      ? null
      : cartItems.find((item) => (
        isRecord(item) &&
        item.quantity > 0 &&
        isRecord(item.properties) &&
        item.properties._is_gift === 'true' &&
        String(item.properties._gift_level) === String(levelNumber)
      )) || null;
    const baseResolution = {
      levelNumber,
      status: null,
      action: null,
      signature,
      giftLineKey: giftLine ? giftLine.key || null : null,
      option: null,
      clearDecision: false,
    };

    if (source.eligible !== true) {
      return {
        ...baseResolution,
        action: 'cleanup',
        clearDecision: true,
      };
    }

    if (giftLine) {
      return {
        ...baseResolution,
        status: 'gift',
        clearDecision: decision != null,
      };
    }

    if (
      isRecord(decision) &&
      decision.decision === 'none' &&
      decision.signature === signature
    ) {
      return {
        ...baseResolution,
        status: 'none',
      };
    }

    const cardinality = resolveOptionCardinality(options);
    const clearDecision = decision != null;

    if (cardinality.mode === 'unavailable') {
      return {
        ...baseResolution,
        status: 'unavailable',
        clearDecision,
      };
    }

    if (cardinality.mode === 'auto') {
      return {
        ...baseResolution,
        action: 'auto-add',
        option: cardinality.option,
        clearDecision,
      };
    }

    return {
      ...baseResolution,
      status: 'pending',
      action: 'choose',
      clearDecision,
    };
  }

  function sortPendingLevels(levelResolutions) {
    if (!Array.isArray(levelResolutions)) {
      return [];
    }

    return levelResolutions
      .filter((resolution) => (
        isRecord(resolution) && resolution.status === 'pending'
      ))
      .sort((first, second) => (
        Number(first.levelNumber) - Number(second.levelNumber)
      ));
  }

  function shouldBlockCheckout(levelResolutions) {
    return Array.isArray(levelResolutions) && levelResolutions.some(
      (resolution) => (
        isRecord(resolution) && resolution.status === 'pending'
      ),
    );
  }

  function resolveCampaignSchedule(input) {
    const source = isRecord(input) ? input : {};

    // Agendamento desligado: o mestre (gift_system_enabled) manda, sempre ativo.
    if (source.enabled !== true) {
      return { active: true, status: 'disabled' };
    }

    const now = Number(source.nowEpoch);

    // Sem relógio confiável: fail-open, não bloqueia a campanha.
    if (!Number.isFinite(now)) {
      return { active: true, status: 'active' };
    }

    const start = source.startEpoch == null ? null : Number(source.startEpoch);
    const end = source.endEpoch == null ? null : Number(source.endEpoch);
    const hasStart = Number.isFinite(start);
    const hasEnd = Number.isFinite(end);

    if (hasStart && now < start) {
      return { active: false, status: 'scheduled' };
    }

    if (hasEnd && now >= end) {
      return { active: false, status: 'ended' };
    }

    return { active: true, status: 'active' };
  }

  return {
    DECISION_STORAGE_KEY,
    normalizeGiftOptions,
    buildOptionSignature,
    resolveOptionCardinality,
    getNoneDecision,
    recordNoneDecision,
    clearNoneDecision,
    resolveLevelState,
    sortPendingLevels,
    shouldBlockCheckout,
    resolveCampaignSchedule,
  };
});
